import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccountDeletionFailure } from "src/domains/myPageContent/accountInfo/user-query/accountDeletion";
import { useWithdrawAccount } from "./useWithdrawAccount";

/**
 * 회원탈퇴 확인 창(`WithdrawDialog`)의 열림·창 안 실패 안내와 확인·닫기. 탈퇴 요청과 처리 중 판정은
 * `useWithdrawAccount` 의 `withdrawAccount`·`isWithdrawingAccount` 에 맡긴다.
 */
export const useAccountWithdrawal = () => {
  const { t } = useTranslation("projects");
  const { withdrawAccount, isWithdrawingAccount } = useWithdrawAccount();
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  // 탈퇴 실패 세 갈래 → 창 안 안내 문구. 409 는 서버 문구를 그대로 쓴다(바꾸지 않는다).
  const toErrorText = (failure: AccountDeletionFailure): string => {
    if (failure.kind === "ownedProject") return failure.message;
    // 재시도 안내는 창의 확인 버튼 이름을 인용한다 — 버튼 라벨 키를 보간해 둘이 어긋나지 않게 한다.
    if (failure.kind === "retry") {
      return t("myPageModal.withdrawal.retryNotice", {
        ns: "projects",
        confirmLabel: t("myPage.withdrawal.confirm", { ns: "projects" }),
      });
    }
    return t("myPage.messages.accountDeleteFailed", { ns: "projects" });
  };

  const close = () => {
    setOpen(false);
    setErrorText(null);
  };

  // 204 면 withdrawAccount 가 완료 안내 뒤 로그아웃으로 화면을 옮기므로 창을 따로 닫지 않는다(그동안
  // isWithdrawingAccount 가 창을 잠가 둔다). 실패는 창을 열어 둔 채 보인다.
  const confirm = () => {
    setErrorText(null);
    withdrawAccount({ onFailure: (failure) => setErrorText(toErrorText(failure)) });
  };

  return {
    open,
    openDialog: () => setOpen(true),
    processing: isWithdrawingAccount,
    errorText,
    close,
    confirm,
  };
};
